import { createSelector } from "reselect"

import { FlowData } from "@tenforce/product-fields"

import { IItemStatus, IStatusTransition } from "../../models"

import * as FlowUtils from "./utils/flow"

import { IFlowPanel } from "."

const getConceptId = (state: IFlowPanel): number => state.conceptId

const getItemId = (state: IFlowPanel): number => state.itemId

const getInitialStatus = (state: IFlowPanel): IItemStatus | null =>
    state.initialStatus

const getFinalStatus = (state: IFlowPanel): IItemStatus | null =>
    state.finalStatus

const getCurrentStatus = (state: IFlowPanel): IItemStatus | null =>
    state.currentStatus

const getStatuses = (state: IFlowPanel): IItemStatus[] => state.statuses

const getStatusTransitions = (state: IFlowPanel): IStatusTransition[] =>
    state.statusTransitions

// the any status is a joker basically
// it means any possible item status for the concept
// so the easiest way to calculate with it is to
// create every possible transition for it
export const getTransitionsWithoutAnyStatuses = createSelector(
    [getStatusTransitions, getStatuses],
    (transitions, statuses): IStatusTransition[] =>
        transitions.flatMap((transition) => {
            const isFromAny =
                transition.fromStatus.code === FlowUtils.anyStatusCode
            const isToAny = transition.toStatus.code === FlowUtils.anyStatusCode

            if (isFromAny && isToAny) {
                // drop * -> *
                return []
            } else if (isFromAny) {
                // Expand "from any status"
                return statuses.map((status) => ({
                    ...transition,
                    fromStatus: status,
                }))
            } else if (isToAny) {
                // Expand "to any status"
                return statuses.map((status) => ({
                    ...transition,
                    toStatus: status,
                }))
            }

            // No "any status" involved, keep as is
            return [transition]
        }),
)

// this is needed because we can actually set from and to to the same status
export const getCleanedTransitions = createSelector(
    [getTransitionsWithoutAnyStatuses],
    (transitions): IStatusTransition[] =>
        transitions.filter(
            (transition) => transition.fromStatus.id !== transition.toStatus.id,
        ),
)

export const isFlowPanelDataAvailable = createSelector(
    [
        getConceptId,
        getItemId,
        getInitialStatus,
        getFinalStatus,
        getCurrentStatus,
        getCleanedTransitions,
    ],
    (
        conceptId,
        itemId,
        initialStatus,
        finalStatus,
        currentstatus,
        statusTransitions,
    ): boolean =>
        conceptId > 0 &&
        itemId > 0 &&
        !!initialStatus &&
        !!finalStatus &&
        !!currentstatus &&
        statusTransitions.length > 0,
)

export const getFlow = createSelector(
    [getCleanedTransitions, getCurrentStatus, getInitialStatus, getFinalStatus],
    (transitions, currentStatus, initialStatus, finalStatus): FlowData => {
        const flow: FlowData = {
            isEndUnknown: false,
            isStartUnknown: false,
            completeNodes: [],
            incompleteNodes: [],
        }

        // if the current status is unknown
        // we cannot return a real flow
        if (
            !!currentStatus &&
            !!initialStatus &&
            !!finalStatus &&
            transitions.length > 0
        ) {
            // find the beginning of the flow
            let registeredCompleteStatuses: IItemStatus[] = []
            let visited = new Set<number>([currentStatus.id])

            // only start looking when we are not in a starting status
            if (currentStatus.id !== initialStatus.id) {
                const previousStatuses = FlowUtils.getPreviousStatuses(
                    currentStatus,
                    transitions,
                )

                for (const previousStatus of previousStatuses) {
                    // if there is only one status in the previous one
                    // the we create a node and continue searching
                    if (previousStatus.length === 1) {
                        const nextStatus = previousStatus[0]
                        // to handle infinite loop, we need to fade out
                        if (visited.has(nextStatus.id)) {
                            flow.isStartUnknown = true
                            break
                        } else {
                            registeredCompleteStatuses.push(nextStatus)
                            visited.add(nextStatus.id)

                            // if this is the initial status, we stop the flow search
                            if (nextStatus.id === initialStatus.id) {
                                break
                            }
                        }
                    }
                    // if there are multiple, then we don't know the start
                    // so we hide the start of the progress bar
                    else if (previousStatus.length > 1) {
                        flow.isStartUnknown = true
                    }
                }
            }

            // because we are looking backwards
            // we need to reverse the results
            // so they will be shown in the correct order
            registeredCompleteStatuses = registeredCompleteStatuses.reverse()

            // the current status counts as a complete node
            registeredCompleteStatuses.push(currentStatus)

            flow.completeNodes = FlowUtils.getFlowNodesByStatuses(
                registeredCompleteStatuses,
                currentStatus,
            )

            if (currentStatus.id !== finalStatus.id) {
                // find the end of the flow
                const registeredUpcomingStatuses: IItemStatus[] = []
                const followingStatuses = FlowUtils.getFollowingStatuses(
                    currentStatus,
                    transitions,
                )

                visited = new Set<number>(
                    registeredCompleteStatuses.map((status) => status.id),
                )

                for (const nextStatuses of followingStatuses) {
                    // if there is only one status in the next one
                    // then we create a node and continue searching
                    if (nextStatuses.length === 1) {
                        const nextStatus = nextStatuses[0]

                        // if we have already been here
                        // then we need to stop the loop
                        // otherwhise this can be an infinite loop
                        // so we fade out
                        if (visited.has(nextStatus.id)) {
                            flow.isEndUnknown = true
                            break
                        }

                        registeredUpcomingStatuses.push(nextStatus)
                        visited.add(nextStatus.id)

                        // if this is the final status, we stop the flow search
                        if (nextStatus.id === finalStatus.id) {
                            break
                        }
                    }
                    // if there are multiple, then we don't know the end
                    // so we hide the end of the progress bar
                    else if (nextStatuses.length > 1) {
                        flow.isEndUnknown = true
                        break
                    }
                }

                flow.incompleteNodes = FlowUtils.getFlowNodesByStatuses(
                    registeredUpcomingStatuses,
                    currentStatus,
                )
            }
        }

        return flow
    },
)
